import React, { useState } from 'react';
import { Modal, Box, Typography, useTheme } from '@mui/material';
import InputField from './InputField';
import Button from './Button';
import DOMPurify from 'dompurify';

interface EmailModalProps {
    open: boolean;
    onClose: () => void;
    onSend: (email: string, message: string) => Promise<void>;
    title: string;
    description: string;
    isSending?: boolean;
}

const EmailModal: React.FC<EmailModalProps> = ({
    open,
    onClose,
    onSend,
    title,
    description,
    isSending = false,
}) => {
    const theme = useTheme();
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [messageError, setMessageError] = useState('');

    const sanitizeUserHtml = (dirty: string) => {
        return DOMPurify.sanitize(dirty, {
            ALLOWED_TAGS: [],
            ALLOWED_ATTR: []
        });
    };

    const handleSend = async () => {
        const trimmedMessage = message.trim();

        if (!trimmedMessage) {
            setMessageError('Message cannot be empty or just spaces');
            return;
        }

        const safeMessage = sanitizeUserHtml(trimmedMessage);

        try {
            await onSend(email, safeMessage);
            setEmail('');
            setMessage('');
            setMessageError('');
        } catch (error) {
            // Handle error if needed
        }
    };

    const handleCancel = () => {
        if (!isSending) {
            onClose();
            setEmail('');
            setMessage('');
            setMessageError('');
        }
    };

    const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setMessage(e.target.value);
        if (messageError && e.target.value.trim()) {
            setMessageError('');
        }
    };

    const sanitizedDescriptionHtml = sanitizeUserHtml(description);

    return (
        <Modal
            open={open}
            onClose={handleCancel}
            aria-labelledby="email-modal-title"
            aria-describedby="email-modal-description"
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '640px',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                    overflowY: 'auto',
                    maxHeight: '501px'
                }}
            >
                <div className="flex flex-col gap-4">
                    <Typography variant="h6" sx={{
                        color: (theme) => theme.palette.text.secondary,
                    }}>{title}</Typography>
                    {/* <Typography variant="body2" color="secondary">
                        {description}
                    </Typography> */}
                    <Typography variant="body2" color="secondary" component="div" dangerouslySetInnerHTML={{ __html: sanitizedDescriptionHtml }} />

                    <InputField
                        label="Email"
                        name="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email"
                        type="email"
                        isMandatory={true}
                        status={email ? 'success' : undefined}
                        isDisabled={isSending}
                    />

                    <div className="flex flex-col w-full">
                        <Typography variant="body2" fontWeight={600} color="textPrimary" className="self-start flex items-center space-x-1">
                            <span>Message</span>
                            <span className="text-red-500" title="Required field">*</span>
                        </Typography>

                        <textarea
                            name="message"
                            value={message}
                            onChange={handleMessageChange}
                            placeholder="Enter your message"
                            rows={4}
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '8px',
                                borderColor: messageError ? theme.palette.error.main : theme.palette.action.selected,
                                fontSize: '14px',
                                outline: 'none',
                                resize: 'vertical',
                                marginTop: '8px',
                            }}
                            disabled={isSending}
                            className='w-full border border-gray-300 rounded-md p-2'
                        />
                        {messageError && (
                            <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                                {messageError}
                            </Typography>
                        )}
                    </div>

                    <div className="flex gap-2 justify-between">
                        <Button
                            text="Cancel"
                            onClick={handleCancel}
                            disabled={isSending}
                            fullWidth={false}
                            variant='outlined'
                        />

                        <Button
                            text={isSending ? "Sending..." : "Send"}
                            onClick={handleSend}
                            disabled={!email || isSending}
                            fullWidth={false}
                        />
                    </div>
                </div>
            </Box>
        </Modal>
    );
};

export default EmailModal;